import i18n from '@risksmart-app/i18n/src/i18n';
import type { Action_Status_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import BadgeList from 'src/components/badge-list';
import { getT1CategoryTitle } from 'src/pages/risks/getT1CategoryTitle';
import { getT2CategoryTitle } from 'src/pages/risks/getT2CategoryTitle';
import type { LabelledIdArray } from 'src/rbac/types';

import { isActionStatusOverdue } from '@/components/action-status-badge/utils';
import type { StatefulTableOptions } from '@/utils/table/hooks/useGetStatelessTableProps';
import { useGetStatelessTableProps } from '@/utils/table/hooks/useGetStatelessTableProps';
import type {
  FieldConfig,
  TableFields,
  TablePropsWithActions,
  TableRecord,
} from '@/utils/table/types';
import { dateColumnFromConfig } from '@/utils/table/utils/dateColumn';

import { UNRATED } from '../../gigawidget/types';
import { defaultClickthroughFilter } from '../dataSourceHelpers';

/**
 * Creates table props for relationship data sources using useGetStatelessTableProps.
 */
export const useRelationshipTableProps = <TRow extends TableRecord>(
  data: TRow[] | undefined,
  fields: TableFields<TRow>,
  entityLabel: string,
  options: StatefulTableOptions<TRow>
): TablePropsWithActions<TRow> => {
  return useGetStatelessTableProps<TRow>({
    data,
    fields,
    entityLabel,
    customAttributeFormIds: [],
    enableFiltering: false,
    ...options,
  });
};

/**
 * Creates a simple field config for a string field.
 */
export const simpleField = <T extends TableRecord>(
  header: string
): FieldConfig<T> => ({
  header,
});

/**
 * Prefixes an attribute label with its entity name when the label doesn't
 * already contain it (RSP-5302). Relational widgets combine fields from two
 * entities, so an ambiguous "Status" or "Type" needs to be disambiguated as
 * "Action Status" or "Control Type". Labels that already embed the entity
 * name (e.g. "Risk tier", "Action title") are returned unchanged so we don't
 * double-prefix to "Risk Risk tier".
 *
 * `entityTaxonomyKey` is a singular taxonomy key (e.g. `"action_one"`) so
 * tenants who rename an entity get the rename applied to these labels too.
 */
export const prefixedLabel = (
  entityTaxonomyKey:
    | 'action_one'
    | 'control_one'
    | 'indicator_one'
    | 'issue_one'
    | 'obligation_one'
    | 'risk_one',
  label: string
): string => {
  const entity = i18n.format(i18n.t(entityTaxonomyKey), 'capitalize');
  if (!label) {
    return entity;
  }
  if (label.toLowerCase().includes(entity.toLowerCase())) {
    return label;
  }

  return `${entity} ${label}`;
};

/**
 * Field config for an enum/rating column where the displayed value is a
 * human-readable label ("Urgent", "High", …) but the column must sort by
 * the underlying numeric severity (1 = Urgent … 4 = Low) rather than the
 * label's alphabetical order ("High, Low, Normal, Urgent"). The display
 * cell still renders the label via the row's string field; Cloudscape
 * uses the numeric accessor purely for sort comparison.
 *
 * Nulls sort last in ascending direction (and therefore first in
 * descending, since Cloudscape negates the comparator). That keeps "No
 * priority" grouped consistently at the ends of the severity order.
 *
 * RSP-5303: widget tables previously sorted by the rendered label,
 * producing alphabetical order that contradicted severity intent. The
 * chart/filter path already uses a `sortKey` on the category getter; this
 * is the equivalent single source of truth for the table path.
 */
export const severitySortedField = <T extends TableRecord>(
  header: string,
  severityAccessor: (item: T) => null | number | undefined
): FieldConfig<T> => ({
  header,
  sortingComparator: (a, b) => {
    const av = severityAccessor(a);
    const bv = severityAccessor(b);
    if (av == null && bv == null) {
      return 0;
    }
    if (av == null) {
      return 1;
    }
    if (bv == null) {
      return -1;
    }

    return av - bv;
  },
});

/**
 * Field config for a `LabelledIdArray` column (e.g. action owners). Renders
 * labels as badges — without a custom `cell`, the table renders the raw
 * `{ label, id }` objects as React children and throws React error #31, which
 * the widget error boundary catches as "Widget content failed to load".
 */
export const ownersField = <T extends TableRecord>(
  header: string,
  accessor: (item: T) => LabelledIdArray
): FieldConfig<T> => ({
  header,
  cell: (item) => <BadgeList badges={accessor(item).map((o) => o.label)} />,
  sortingComparator: (a, b) =>
    accessor(a)
      .map((o) => o.label)
      .join(',')
      .localeCompare(
        accessor(b)
          .map((o) => o.label)
          .join(',')
      ),
  exportVal: (item) =>
    accessor(item)
      .map((o) => o.label)
      .join(','),
});

/**
 * Returns the localized "Overdue" / "On track" label for an action row.
 * Mirrors `isActionStatusOverdue` used by the single-entity action source, but
 * exposes overdue as an independent category so relationship widgets can chart
 * Status and Overdue separately (AC 2).
 */
export const getActionOverdueLabel = (
  action:
    | { DateDue?: null | string; Status?: Action_Status_Enum | null }
    | undefined
): string => {
  const isOverdue =
    action?.Status && action.DateDue
      ? isActionStatusOverdue({
          item: {
            Status: action.Status,
            DateDue: action.DateDue,
          },
        })
      : false;

  return isOverdue
    ? i18n.t('actions.columns.overdue')
    : i18n.t('actions.columns.on_track');
};

/**
 * The indicator-side shape shared by every indicator relational data source
 * (indicatorRisk, indicatorControl, indicatorObligation — RSP-5305/06/07).
 *
 * The first six fields (Frequency → TestScheduleStatus) are chartable
 * category getters. The remainder are filter-only attributes surfaced via
 * the property-filter `fields` config, not as chart axes.
 */
export type IndicatorRowSlice = {
  IndicatorId: string;
  IndicatorFriendlyId: string;
  IndicatorTitle: string;
  IndicatorFrequency: string;
  IndicatorConformance: string;
  IndicatorConformanceTrend: string;
  IndicatorLatestResult: string;
  IndicatorUnit: string;
  IndicatorTestScheduleStatus: string;
  // Filter-only attributes per ticket AC-2.
  IndicatorParent: string;
  IndicatorPreviousResult: string;
  IndicatorLatestResultDate: string;
  IndicatorNextTestDate: string;
  IndicatorNextTestOverdueDate: string;
  IndicatorCreatedOn: string;
  IndicatorUpdatedOn: string;
  IndicatorLowerTolerance: null | number;
  IndicatorUpperTolerance: null | number;
  IndicatorLowerAppetite: null | number;
  IndicatorUpperAppetite: null | number;
};

/**
 * Shared indicator-side table field config. Spread into each indicator
 * relational source's `fields` map alongside the partner-entity columns.
 * Headers are `prefixedLabel`-wrapped so they render as "Indicator Title"
 * etc. even when the ambient taxonomy doesn't include "indicator" in the
 * raw i18n value.
 */
export const indicatorSideFieldConfig = <
  T extends IndicatorRowSlice & TableRecord,
>(): TableFields<T> =>
  ({
    IndicatorFriendlyId: simpleField(
      prefixedLabel('indicator_one', i18n.t('columns.id'))
    ),
    IndicatorTitle: simpleField(
      prefixedLabel('indicator_one', i18n.t('columns.title'))
    ),
    IndicatorFrequency: simpleField(
      prefixedLabel(
        'indicator_one',
        i18n.t('indicators.columns.test_frequency')
      )
    ),
    IndicatorConformance: simpleField(
      prefixedLabel('indicator_one', i18n.t('indicators.columns.conformance'))
    ),
    IndicatorConformanceTrend: simpleField(
      prefixedLabel(
        'indicator_one',
        i18n.t('indicators.columns.conformance_trend')
      )
    ),
    IndicatorLatestResult: simpleField(
      prefixedLabel('indicator_one', i18n.t('indicators.columns.latest_result'))
    ),
    IndicatorUnit: simpleField(
      prefixedLabel('indicator_one', i18n.t('indicators.columns.unit'))
    ),
    IndicatorTestScheduleStatus: simpleField(
      prefixedLabel(
        'indicator_one',
        i18n.t('indicators.columns.testScheduleStatus')
      )
    ),
    // Filter-only attributes.
    IndicatorParent: simpleField(
      prefixedLabel('indicator_one', i18n.t('indicators.columns.parent_title'))
    ),
    IndicatorPreviousResult: simpleField(
      prefixedLabel(
        'indicator_one',
        i18n.t('indicators.columns.previous_result')
      )
    ),
    IndicatorLatestResultDate: dateColumnFromConfig<T>({
      header: {
        header: prefixedLabel(
          'indicator_one',
          i18n.t('indicators.columns.latest_result_date')
        ),
      },
      dateField: 'IndicatorLatestResultDate' as Extract<keyof T, string>,
    }),
    IndicatorNextTestDate: dateColumnFromConfig<T>({
      header: {
        header: prefixedLabel(
          'indicator_one',
          i18n.t('indicators.columns.nextTestDate')
        ),
      },
      dateField: 'IndicatorNextTestDate' as Extract<keyof T, string>,
    }),
    IndicatorNextTestOverdueDate: dateColumnFromConfig<T>({
      header: {
        header: prefixedLabel(
          'indicator_one',
          i18n.t('indicators.columns.nextTestOverdue')
        ),
      },
      dateField: 'IndicatorNextTestOverdueDate' as Extract<keyof T, string>,
    }),
    IndicatorCreatedOn: dateColumnFromConfig<T>({
      header: {
        header: prefixedLabel('indicator_one', i18n.t('columns.created_on')),
      },
      dateField: 'IndicatorCreatedOn' as Extract<keyof T, string>,
    }),
    IndicatorUpdatedOn: dateColumnFromConfig<T>({
      header: {
        header: prefixedLabel(
          'indicator_one',
          i18n.t('indicators.columns.updated_on')
        ),
      },
      dateField: 'IndicatorUpdatedOn' as Extract<keyof T, string>,
    }),
    IndicatorLowerTolerance: simpleField(
      prefixedLabel(
        'indicator_one',
        i18n.t('indicators.columns.lower_tolerance_num')
      )
    ),
    IndicatorUpperTolerance: simpleField(
      prefixedLabel(
        'indicator_one',
        i18n.t('indicators.columns.upper_tolerance_num')
      )
    ),
    IndicatorLowerAppetite: simpleField(
      prefixedLabel(
        'indicator_one',
        i18n.t('indicators.columns.lower_appetite_num')
      )
    ),
    IndicatorUpperAppetite: simpleField(
      prefixedLabel(
        'indicator_one',
        i18n.t('indicators.columns.upper_appetite_num')
      )
    ),
  }) as TableFields<T>;

/**
 * Shared indicator-side category getters. Returned as an array so each
 * indicator relational source can spread them into its own `categoryGetters`
 * alongside the partner-entity getters.
 *
 * Filter-only attributes are not exposed here — they live in the `fields`
 * config (property-filter surface) only, per ticket AC-2.
 */
export const indicatorSideCategoryGetters = <T extends IndicatorRowSlice>() => [
  {
    id: 'indicatorFrequency',
    entityGroup: 'indicator_one' as const,
    name: () =>
      prefixedLabel(
        'indicator_one',
        i18n.t('indicators.columns.test_frequency')
      ),
    categoryGetter: (item: T) => item.IndicatorFrequency || UNRATED,
    clickthroughFilter: defaultClickthroughFilter('IndicatorFrequency'),
  },
  {
    id: 'indicatorConformance',
    entityGroup: 'indicator_one' as const,
    name: () =>
      prefixedLabel('indicator_one', i18n.t('indicators.columns.conformance')),
    categoryGetter: (item: T) => item.IndicatorConformance || UNRATED,
    clickthroughFilter: defaultClickthroughFilter('IndicatorConformance'),
    ratingColourKey: 'indicator_conformance_status' as const,
  },
  {
    id: 'indicatorConformanceTrend',
    entityGroup: 'indicator_one' as const,
    name: () =>
      prefixedLabel(
        'indicator_one',
        i18n.t('indicators.columns.conformance_trend')
      ),
    categoryGetter: (item: T) => item.IndicatorConformanceTrend || UNRATED,
    clickthroughFilter: defaultClickthroughFilter('IndicatorConformanceTrend'),
    ratingColourKey: 'indicator_conformance_trend' as const,
  },
  {
    id: 'indicatorLatestResult',
    entityGroup: 'indicator_one' as const,
    name: () =>
      prefixedLabel(
        'indicator_one',
        i18n.t('indicators.columns.latest_result')
      ),
    categoryGetter: (item: T) => item.IndicatorLatestResult || UNRATED,
    clickthroughFilter: defaultClickthroughFilter('IndicatorLatestResult'),
  },
  {
    id: 'indicatorUnit',
    entityGroup: 'indicator_one' as const,
    name: () =>
      prefixedLabel('indicator_one', i18n.t('indicators.columns.unit')),
    categoryGetter: (item: T) => item.IndicatorUnit || UNRATED,
    clickthroughFilter: defaultClickthroughFilter('IndicatorUnit'),
  },
  {
    id: 'indicatorTestScheduleStatus',
    entityGroup: 'indicator_one' as const,
    name: () =>
      prefixedLabel(
        'indicator_one',
        i18n.t('indicators.columns.testScheduleStatus')
      ),
    categoryGetter: (item: T) => item.IndicatorTestScheduleStatus || UNRATED,
    clickthroughFilter: defaultClickthroughFilter(
      'IndicatorTestScheduleStatus'
    ),
  },
];

/**
 * Resolves the indicator-side row fields for a single labelled indicator
 * record. Used in each source's `joinedData` mapper to keep the indicator
 * projection identical across all three sources.
 */
export const resolveIndicatorSideRow = (indicator: {
  Id: string;
  SequentialIdLabel?: null | string;
  Title?: null | string;
  ParentTitle?: null | string;
  TestFrequencyLabelled?: null | string;
  ConformanceLabelled?: number | string | null | undefined;
  ConformanceTrend?: null | string;
  LatestResultLabelled?: null | string;
  PreviousResultLabelled?: null | string;
  LatestResultDateLabelled?: null | string;
  Unit?: null | string;
  TestScheduleStatusLabelled?: null | string;
  NextTestDate?: null | string;
  NextTestOverdueDate?: null | string;
  CreatedAtTimestamp?: null | string;
  ModifiedAtTimestamp?: null | string;
  LowerToleranceNum?: null | number;
  UpperToleranceNum?: null | number;
  LowerAppetiteNum?: null | number;
  UpperAppetiteNum?: null | number;
}): IndicatorRowSlice => ({
  IndicatorId: indicator.Id,
  IndicatorFriendlyId: indicator.SequentialIdLabel ?? '',
  IndicatorTitle: indicator.Title ?? '',
  IndicatorFrequency: indicator.TestFrequencyLabelled ?? '',
  IndicatorConformance: String(indicator.ConformanceLabelled ?? ''),
  IndicatorConformanceTrend: indicator.ConformanceTrend ?? '',
  IndicatorLatestResult: indicator.LatestResultLabelled ?? '',
  IndicatorUnit: indicator.Unit ?? '',
  IndicatorTestScheduleStatus: indicator.TestScheduleStatusLabelled ?? '',
  IndicatorParent: indicator.ParentTitle ?? '',
  IndicatorPreviousResult: indicator.PreviousResultLabelled ?? '',
  IndicatorLatestResultDate: indicator.LatestResultDateLabelled ?? '',
  IndicatorNextTestDate: indicator.NextTestDate ?? '',
  IndicatorNextTestOverdueDate: indicator.NextTestOverdueDate ?? '',
  IndicatorCreatedOn: indicator.CreatedAtTimestamp ?? '',
  IndicatorUpdatedOn: indicator.ModifiedAtTimestamp ?? '',
  IndicatorLowerTolerance: indicator.LowerToleranceNum ?? null,
  IndicatorUpperTolerance: indicator.UpperToleranceNum ?? null,
  IndicatorLowerAppetite: indicator.LowerAppetiteNum ?? null,
  IndicatorUpperAppetite: indicator.UpperAppetiteNum ?? null,
});

type RiskHierarchyNode = {
  Id: string;
  ParentRiskId?: null | string;
  Tier: number;
  Title: string;
};

type RiskForTierCategoryAndEntity = RiskHierarchyNode & {
  enterpriseRiskInstance?: { entity?: { Name: string } | null } | null;
};

export type RiskTierCategorySlice = {
  RiskEntity: string;
  RiskT1Category: string;
  RiskT2Category: string;
};

// `useGetLabelledFields` sets `Entity` to `'-'`, others to `''`; either would reach a row
// as a category and as a selectable filter value.
const UNASSIGNED_PLACEHOLDERS = new Set(['', '-', '–', '—']);

const toCategoryValue = (value: null | string | undefined): string =>
  !value || UNASSIGNED_PLACEHOLDERS.has(value.trim()) ? UNRATED : value;

// `UNRATED` goes on the row, not just the getter, so row value, category key, filter
// option and clickthrough predicate all agree.
export const deriveRiskTierCategoryAndEntity = (
  risk: RiskForTierCategoryAndEntity | undefined,
  riskById: Map<string, RiskHierarchyNode>
): RiskTierCategorySlice => ({
  RiskEntity: toCategoryValue(risk?.enterpriseRiskInstance?.entity?.Name),
  RiskT1Category: toCategoryValue(
    risk ? getT1CategoryTitle(risk, riskById) : null
  ),
  RiskT2Category: toCategoryValue(
    risk ? getT2CategoryTitle(risk, riskById) : null
  ),
});

// Presence in `fields` is what reaches the Filter selector; getters only reach
// Category/Sub-category. Spread both.
export const riskTierCategoryAndEntityFieldConfig = <
  T extends RiskTierCategorySlice & TableRecord,
>(): TableFields<T> =>
  ({
    RiskEntity: simpleField(
      prefixedLabel('risk_one', i18n.t('risks.columns.entity'))
    ),
    RiskT1Category: simpleField(
      prefixedLabel('risk_one', i18n.t('risks.columns.t1_category'))
    ),
    RiskT2Category: simpleField(
      prefixedLabel('risk_one', i18n.t('risks.columns.t2_category'))
    ),
  }) as TableFields<T>;

// `entityGroup` mandatory: once one getter declares it, getters without it are dropped
// from the selector entirely (RSP-5299).
export const riskTierCategoryAndEntityGetters = <
  T extends RiskTierCategorySlice,
>() => [
  {
    id: 'riskT1Category',
    entityGroup: 'risk_one' as const,
    name: () => prefixedLabel('risk_one', i18n.t('risks.columns.t1_category')),
    categoryGetter: (item: T) => item.RiskT1Category || UNRATED,
    clickthroughFilter: defaultClickthroughFilter('RiskT1Category'),
  },
  {
    id: 'riskT2Category',
    entityGroup: 'risk_one' as const,
    name: () => prefixedLabel('risk_one', i18n.t('risks.columns.t2_category')),
    categoryGetter: (item: T) => item.RiskT2Category || UNRATED,
    clickthroughFilter: defaultClickthroughFilter('RiskT2Category'),
  },
  {
    id: 'riskEntity',
    entityGroup: 'risk_one' as const,
    name: () => prefixedLabel('risk_one', i18n.t('risks.columns.entity')),
    categoryGetter: (item: T) => item.RiskEntity || UNRATED,
    clickthroughFilter: defaultClickthroughFilter('RiskEntity'),
  },
];
